import { Fragment, useState } from "react";
import { formatDateTime, ORDER_STATUSES, setOrderStatus } from "../../services/adminService";
import { useInbox } from "./Inbox";
import { useToast } from "./Toast";
import RawDetails from "./RawDetails";

const LABEL = Object.fromEntries(ORDER_STATUSES);

export default function ShopOrders() {
  const { orders, ready, errors, newOrders } = useInbox();
  const toast = useToast();
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const counts = ORDER_STATUSES.reduce((acc, [key]) => {
    acc[key] = orders.filter((o) => o.status === key).length;
    return acc;
  }, {});
  const shown = orders.filter((o) => filter === "all" || o.status === filter);

  const change = async (order, status) => {
    if (status === order.status) return;
    setBusyId(order.id);
    try {
      await setOrderStatus(order, status);
      toast.show(`Order from ${order.customer} marked ${LABEL[status].toLowerCase()}`);
    } catch (err) {
      console.error(err);
      toast.show("Could not update this order", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <header className="na-page-head">
        <div>
          <h1>Shop orders</h1>
          <p className="na-muted">Orders placed on the website appear here the moment they are submitted.</p>
        </div>
      </header>

      <section className="na-stats">
        <button type="button" className={`na-card na-stat ${filter === "all" ? "is-active" : ""}`} onClick={() => setFilter("all")}>
          <span className="na-muted">All orders</span>
          <strong>{orders.length}</strong>
          <small>{newOrders} waiting to be handled</small>
        </button>
        {ORDER_STATUSES.filter(([k]) => k !== "cancelled").map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`na-card na-stat ${filter === key ? "is-active" : ""}`}
            onClick={() => setFilter((f) => (f === key ? "all" : key))}
          >
            <span className="na-muted">{label}</span>
            <strong>{counts[key]}</strong>
            <small>{key === "new" ? "Not yet looked at" : key === "processing" ? "Being prepared" : "Handed over"}</small>
          </button>
        ))}
      </section>

      {errors.orders && <p className="na-error">{errors.orders}</p>}

      <section className="na-card na-table-card">
        <div className="na-table-wrap">
          <table className="na-table">
            <thead>
              <tr><th>Customer</th><th>Items</th><th>Total</th><th>Placed</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {!ready.orders && <tr><td colSpan="6" className="na-empty">Loading orders…</td></tr>}
              {ready.orders && shown.length === 0 && (
                <tr>
                  <td colSpan="6" className="na-empty">
                    <p>{orders.length === 0 ? "No orders yet." : "No orders with this status."}</p>
                    {orders.length === 0 && <p className="na-small">If someone has ordered and nothing shows, the Shop page may not be saving to the merchOrders collection.</p>}
                  </td>
                </tr>
              )}
              {shown.map((o) => (
                <Fragment key={o.id}>
                  <tr>
                    <td>
                      <strong>{o.customer}</strong>
                      {o.orderNo && <small className="na-block">Order {o.orderNo}</small>}
                      <small className="na-block">{[o.email, o.phone].filter(Boolean).join(" · ")}</small>
                    </td>
                    <td>
                      {o.items.length === 0 && "—"}
                      {o.items.map((it, i) => (
                        <div key={i} className="na-item">
                          {it.qty} × {it.name}
                          {it.opts && <small> ({it.opts})</small>}
                        </div>
                      ))}
                    </td>
                    <td>{o.total ? `${o.currency} ${o.total.toLocaleString()}` : "—"}</td>
                    <td>{formatDateTime(o.createdAt)}</td>
                    <td><span className={`na-order na-order-${o.status}`}>{LABEL[o.status] || o.status}</span></td>
                    <td className="na-actions">
                      <select
                        value={o.status}
                        disabled={busyId === o.id}
                        onChange={(e) => change(o, e.target.value)}
                        aria-label={`Status for order from ${o.customer}`}
                      >
                        {ORDER_STATUSES.map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                      <button onClick={() => setOpenId(openId === o.id ? null : o.id)}>
                        {openId === o.id ? "Hide" : "Details"}
                      </button>
                    </td>
                  </tr>
                  {openId === o.id && (
                    <tr className="na-detail-row">
                      <td colSpan="6">
                        {o.address && <p><strong>Delivery:</strong> {o.address}</p>}
                        {o.notes && <p><strong>Notes:</strong> {o.notes}</p>}
                        <RawDetails data={o.raw} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
